/**
 * Phase K — /admin/seo settings page.
 *
 * Admin-only form for editing site_settings (single doc). Unset fields must
 * remain null so the SEO helper doesn't render bad meta tags.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function AdminSEO() {
  const [form, setForm] = useState({
    google_search_console_verification: "",
    bing_webmaster_verification: "",
    default_og_image_file_id: "",
    twitter_handle: "",
    site_url: "",
    ilc_intake_email: "",
    ilc_partnership_visible: true,
    internal_notify_email: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState({ title: "Beyond Valencia", description: "Florida transfer admissions strategy." });
  const [pinging, setPinging] = useState(false);
  const [pingResult, setPingResult] = useState(null);
  const [pingError, setPingError] = useState(null);

  useEffect(() => {
    api.get("/admin/seo/settings").then((r) => {
      const d = r.data || {};
      setForm({
        google_search_console_verification: d.google_search_console_verification || "",
        bing_webmaster_verification: d.bing_webmaster_verification || "",
        default_og_image_file_id: d.default_og_image_file_id || "",
        twitter_handle: d.twitter_handle || "",
        site_url: d.site_url || "",
        ilc_intake_email: d.ilc_intake_email || "",
        ilc_partnership_visible: d.ilc_partnership_visible !== false,
        internal_notify_email: d.internal_notify_email || "",
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (typeof v === "boolean") {
          payload[k] = v;
        } else {
          const trimmed = (v || "").trim();
          payload[k] = trimmed || null;
        }
      }
      await api.post("/admin/seo/settings", payload);
      toast.success("SEO settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-ink-soft">Loading SEO settings…</div>;

  const ogImageUrl = form.default_og_image_file_id ? `/api/media/${form.default_og_image_file_id}` : null;

  const pingEngines = async () => {
    setPinging(true);
    setPingError(null);
    setPingResult(null);
    try {
      const r = await api.post("/admin/seo/ping-search-engines");
      setPingResult(r.data);
      toast.success("Search engines notified");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429 && detail && typeof detail === "object") {
        setPingError({
          message: detail.message || "Rate-limited to once per hour.",
          retry_after_seconds: detail.retry_after_seconds,
        });
      } else {
        setPingError({ message: (typeof detail === "string" ? detail : "Ping failed. Check backend logs.") });
      }
      toast.error("Ping failed");
    } finally {
      setPinging(false);
    }
  };

  return (
    <div data-testid="admin-seo-page">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl text-ivy tracking-tight">SEO</h1>
          <p className="text-sm text-stone mt-1">
            Site-wide SEO settings. Empty fields are OK — the corresponding meta tag simply isn&apos;t rendered.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="bg-ivy hover:bg-ivy/90 text-bone uppercase tracking-[0.18em] text-xs font-semibold py-3 px-5 disabled:opacity-50"
          data-testid="admin-seo-save"
        >
          {saving ? "Saving…" : "Save settings"}
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-5">
          <Field
            label="Google Search Console verification code"
            hint="The content= value from the meta tag Google gives you. Rendered as <meta name='google-site-verification'>."
            value={form.google_search_console_verification}
            onChange={(v) => setForm({ ...form, google_search_console_verification: v })}
            testId="admin-seo-gsc"
          />
          <Field
            label="Bing Webmaster verification code"
            hint="The content= value from the meta tag Bing gives you. Rendered as <meta name='msvalidate.01'>."
            value={form.bing_webmaster_verification}
            onChange={(v) => setForm({ ...form, bing_webmaster_verification: v })}
            testId="admin-seo-bing"
          />
          <Field
            label="Default OG image file_id"
            hint={<>The file_id of an image uploaded via <a href="/admin/media" className="text-ivy underline">/admin/media</a>. Rendered as og:image on pages that don&apos;t set their own.</>}
            value={form.default_og_image_file_id}
            onChange={(v) => setForm({ ...form, default_og_image_file_id: v })}
            testId="admin-seo-ogimage"
            placeholder="file_xxxxxxxxxxxx"
          />
          <Field
            label="Twitter handle"
            hint="Rendered as twitter:site. Include the @ prefix."
            value={form.twitter_handle}
            onChange={(v) => setForm({ ...form, twitter_handle: v })}
            testId="admin-seo-twitter"
            placeholder="@ivyleaguecons"
          />
          <Field
            label="Canonical site URL"
            hint="Overrides the default canonical origin (default: https://ivyleagueconsulting.org)."
            value={form.site_url}
            onChange={(v) => setForm({ ...form, site_url: v })}
            testId="admin-seo-siteurl"
            placeholder="https://ivyleagueconsulting.org"
          />

          {/* Phase L — Partnership subsection */}
          <div className="pt-6 mt-6 border-t border-brass/40" data-testid="admin-seo-partnership-section">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">Partnership</div>
            <p className="text-[11px] text-stone mt-1">Ivy League Consulting partnership. Kill-switch here hides the marker everywhere; intake email receives ILC-routed leads.</p>
            <div className="mt-4 space-y-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.ilc_partnership_visible}
                  onChange={(e) => setForm({ ...form, ilc_partnership_visible: e.target.checked })}
                  className="h-4 w-4 accent-ivy"
                  data-testid="admin-seo-ilc-visible"
                />
                <span className="text-sm text-ink">Partnership visible sitewide (kill-switch off)</span>
              </label>
              <Field
                label="ILC intake email (private — never exposed to public API)"
                hint="Recipient for the ilc_lead_routed_v1 email when a lead routes to the partner."
                value={form.ilc_intake_email}
                onChange={(v) => setForm({ ...form, ilc_intake_email: v })}
                testId="admin-seo-ilc-email"
                placeholder="intake@ivyleagueconsulting.org"
              />
              <Field
                label="Beyond Valencia internal notify email"
                hint="Recipient for bv_lead_captured_v1 when a Florida-only lead comes in through /mishap-corrector/unsupported."
                value={form.internal_notify_email}
                onChange={(v) => setForm({ ...form, internal_notify_email: v })}
                testId="admin-seo-internal-email"
                placeholder="leads@beyondvalencia.com"
              />
            </div>
          </div>
        </div>

        <aside className="space-y-6" data-testid="admin-seo-preview">
          <div className="border border-brass/50 bg-bone p-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Preview — meta tags</div>
            <div className="mt-3 text-sm">
              <div className="text-ink font-serif text-lg">{preview.title}</div>
              <div className="text-ink-soft text-xs">{preview.description}</div>
              <div className="text-[10px] text-stone mt-3 uppercase tracking-[0.18em]">og:image</div>
              {ogImageUrl ? (
                <div className="mt-2 border border-stone/30 aspect-video bg-bone-dim overflow-hidden flex items-center justify-center">
                  <img src={ogImageUrl} alt="OG preview" className="max-w-full max-h-full object-contain" />
                </div>
              ) : (
                <div className="mt-2 border border-stone/30 aspect-video bg-bone-dim flex items-center justify-center text-stone text-xs italic">
                  using site default /og-default.png
                </div>
              )}
            </div>
          </div>

          <div className="border border-stone/40 bg-bone p-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Verification status</div>
            <VerRow label="Google Search Console" set={!!form.google_search_console_verification}
              action={<a href="https://search.google.com/search-console/welcome" target="_blank" rel="noopener noreferrer"
                        className="text-[11px] uppercase tracking-[0.18em] text-brass hover:text-ivy" data-testid="admin-seo-test-gsc">Test →</a>} />
            <VerRow label="Bing" set={!!form.bing_webmaster_verification}
              action={<a href="https://www.bing.com/webmasters/home" target="_blank" rel="noopener noreferrer"
                        className="text-[11px] uppercase tracking-[0.18em] text-brass hover:text-ivy" data-testid="admin-seo-test-bing">Test →</a>} />
            <VerRow label="OG image" set={!!form.default_og_image_file_id} action={null} />
          </div>
        </aside>
      </div>

      {/* ---------- Ping search engines ---------- */}
      <section className="mt-14 border border-brass/60 bg-bone p-6" data-testid="admin-seo-ping-section">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Ping search engines</div>
            <h2 className="mt-1 font-serif text-2xl text-ivy tracking-tight">Notify Bing + IndexNow of a sitemap update</h2>
            <p className="mt-2 text-xs text-stone max-w-2xl">
              Run this after publishing large content updates. Rate-limited to once per hour. Google&apos;s sitemap ping was deprecated in 2023 — submit via Search Console directly for Google coverage.
            </p>
          </div>
          <button
            onClick={pingEngines}
            disabled={pinging}
            className="border border-ivy bg-ivy text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-3 px-6 hover:bg-brass hover:border-brass disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="admin-seo-ping-btn"
          >
            {pinging ? "Pinging…" : "Notify search engines"}
          </button>
        </div>

        {pingError && (
          <div className="mt-5 border border-crimson/40 bg-crimson/5 p-4 text-sm text-crimson" data-testid="admin-seo-ping-error">
            {pingError.message}
            {pingError.retry_after_seconds != null && (
              <div className="text-[11px] text-crimson/80 mt-1">
                Try again in ~{Math.ceil(pingError.retry_after_seconds / 60)} minutes.
              </div>
            )}
          </div>
        )}

        {pingResult && (
          <div className="mt-5 grid sm:grid-cols-3 gap-3" data-testid="admin-seo-ping-results">
            <PingCard engine="Bing" result={pingResult.bing} testId="admin-seo-ping-bing" />
            <PingCard engine="IndexNow" result={pingResult.indexnow} testId="admin-seo-ping-indexnow"
                      subtitle="Bing · Yandex · Seznam" />
            <PingCard engine="Google" result={pingResult.google} testId="admin-seo-ping-google"
                      extra={
                        <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer"
                           className="text-[11px] uppercase tracking-[0.18em] text-brass hover:text-ivy"
                           data-testid="admin-seo-ping-google-console-link">
                          Open Search Console →
                        </a>
                      } />
            <div className="col-span-full text-[11px] text-stone">
              Sitemap: <span className="font-mono text-ink-soft">{pingResult.sitemap_url}</span>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function Field({ label, hint, value, onChange, testId, placeholder }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">{label}</label>
      <input
        type="text"
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full border border-stone/40 bg-bone px-3 py-2 text-sm text-ink placeholder:text-stone focus:outline-none focus:border-ivy"
        data-testid={testId}
      />
      {hint && <p className="text-[11px] text-stone mt-2">{hint}</p>}
    </div>
  );
}

function VerRow({ label, set, action }) {
  return (
    <div className="mt-3 flex items-center justify-between border-t border-stone/20 pt-3 first:border-t-0 first:pt-0 first:mt-0">
      <div>
        <div className="text-sm text-ink">{label}</div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-stone">{set ? "code set" : "not configured"}</div>
      </div>
      {action}
    </div>
  );
}

function PingCard({ engine, result, subtitle, extra, testId }) {
  const status = (result && result.status) || "unknown";
  const cfg = {
    ok:         { icon: "✓", cls: "text-ivy border-ivy/60", label: "Notified" },
    deprecated: { icon: "–", cls: "text-stone border-stone/40", label: "Deprecated by provider" },
    skipped:    { icon: "–", cls: "text-stone border-stone/40", label: "Skipped" },
    failed:     { icon: "✕", cls: "text-crimson border-crimson/40", label: "Failed" },
    unknown:    { icon: "?", cls: "text-stone border-stone/40", label: "Unknown" },
  }[status] || { icon: "?", cls: "text-stone border-stone/40", label: status };
  return (
    <div className={`border p-4 bg-bone ${cfg.cls}`} data-testid={testId} data-status={status}>
      <div className="flex items-center gap-2">
        <span className={`font-serif text-2xl ${cfg.cls}`} aria-hidden="true">{cfg.icon}</span>
        <div>
          <div className="font-serif text-lg text-ivy leading-tight">{engine}</div>
          {subtitle && <div className="text-[10px] uppercase tracking-[0.18em] text-brass">{subtitle}</div>}
        </div>
        <div className="ml-auto text-[10px] uppercase tracking-[0.18em] text-stone">{cfg.label}</div>
      </div>
      <p className="mt-3 text-xs text-ink-soft leading-relaxed">{result && result.message}</p>
      {result && result.http_code != null && (
        <div className="mt-2 text-[10px] text-stone tabular-nums">HTTP {result.http_code}</div>
      )}
      {extra}
    </div>
  );
}
